import type { SharedApiV2 } from "@capstone/mock-api";
import { Day } from "src/app/core/day.model";
import { formatDay } from "src/app/core/pipes/day.pipe";
import { formatWeekdays } from "src/app/core/pipes/weekdays.pipe";
import { Site } from "src/app/core/sites";
import { Weekdays } from "src/app/core/weekdays.model";
import { getApiDetailsDecorator } from "src/utils";

const api = getApiDetailsDecorator<SharedApiV2.DockCapacityDto>();

abstract class BaseDailyDockCapacityV2 {
  protected constructor(args: ClassProperties<BaseDailyDockCapacityV2>) {
    this.appointment = args.appointment;
    this.case = args.case;
    this.id = args.id;
    this.pallet = args.pallet;
    this.weekdays = args.weekdays;
    this.site = args.site;
  }

  @api({ key: "appointmentCapacity" })
  public readonly appointment: number | null;
  @api({ key: "caseCapacity" }) public readonly case: number | null;
  @api({ key: "palletCapacity" }) public readonly pallet: number | null;
  @api({ key: "dayOfWeek" }) public readonly weekdays: Weekdays;
  @api() public readonly id: number;
  public readonly site: Site;
}

export class DailyDockCapacityV2 extends BaseDailyDockCapacityV2 {
  private constructor(args: ClassProperties<DailyDockCapacityV2>) {
    super(args);
  }

  public static deserialize(
    data: SharedApiV2.DockCapacityDto,
    { site }: DeserializeArguments,
  ): DailyDockCapacityV2 | DailyDockCapacityExceptionV2 {
    return data.precedence > 0
      ? DailyDockCapacityExceptionV2.deserialize(data, { site })
      : new DailyDockCapacityV2({
          appointment: data.appointmentCapacity ?? null,
          case: data.caseCapacity ?? null,
          id: data.id,
          pallet: data.palletCapacity ?? null,
          weekdays: Weekdays.deserialize(data.dayOfWeek),
          site,
        });
  }
}

export class DailyDockCapacityExceptionV2 extends BaseDailyDockCapacityV2 {
  private constructor(
    args: Omit<ClassProperties<DailyDockCapacityExceptionV2>, "name">,
  ) {
    super(args);
    this.customName = args.customName;
    this.endDay = args.endDay;
    this.precedence = args.precedence;
    this.startDay = args.startDay;

    const formattedStart = formatDay(this.startDay, "en-US", "shortDate");
    const formattedEnd = formatDay(this.endDay, "en-US", "shortDate");
    const formattedWeekdays = formatWeekdays(this.weekdays, "short");

    // Computed properties
    this.name =
      this.customName ||
      `${formattedStart}–${formattedEnd}: ${formattedWeekdays}`;
  }

  @api({ key: "name" }) public readonly customName: string | null;
  @api({ key: "endDate" }) public readonly endDay: Day;
  /**
   * The precedence order (increasing value for higher precedence) that an
   * exception has, with `0` reserved for regular, non-exception capacities.
   */
  @api() public readonly precedence: number;
  @api({ key: "startDate" }) public readonly startDay: Day;

  // Computed properties
  public readonly name: string;

  public static deserialize(
    data: SharedApiV2.DockCapacityDto,
    { site }: DeserializeArguments,
  ): DailyDockCapacityExceptionV2 {
    if (data.precedence === 0) {
      throw new Error(
        "Daily dock capacity exceptions cannot have a zero precedence.",
      );
    }
    if (!data.startDate) {
      throw new Error("Start date required for daily dock capacity.");
    }
    if (!data.endDate) {
      throw new Error("End date required for daily dock capacity.");
    }

    return new DailyDockCapacityExceptionV2({
      appointment: data.appointmentCapacity ?? null,
      case: data.caseCapacity ?? null,
      customName: data.name || null,
      // TODO: update the deserialization to use the site time zone once implemented in the API
      // We deserialize the date to UTC as API ignores the site time zone.
      endDay: Day.deserialize(data.endDate, Day.utc),
      id: data.id,
      pallet: data.palletCapacity ?? null,
      precedence: data.precedence,
      // TODO: update the deserialization to use the site time zone once implemented in the API
      // We deserialize the date to UTC as API ignores the site time zone.
      startDay: Day.deserialize(data.startDate, Day.utc),
      weekdays: Weekdays.deserialize(data.dayOfWeek),
      site,
    });
  }
}

interface DeserializeArguments {
  readonly site: Site;
}
